import { getPublicGtmId } from "@/config/public-runtime-config";
import {
  ALL_CONSENT_MODE_SIGNALS,
  ConsentGroup,
  GTM_GROUPS,
  consentModeState,
  type ConsentModeValue,
  type ConsentState,
} from "@/lib/consent";

type GtmWindow = typeof window & {
  dataLayer?: unknown[];
  __gtmLoaded?: boolean;
};

/**
 * Google's canonical shim. Consent Mode reads the raw `arguments` object, so
 * this has to stay a plain function rather than push a rest array.
 */
function gtagShim(): void {
  const w = window as GtmWindow;
  w.dataLayer = w.dataLayer || [];
  // eslint-disable-next-line prefer-rest-params
  w.dataLayer.push(arguments);
}

const gtag = gtagShim as (...args: unknown[]) => void;

/** How long Google should hold tags waiting for our `update` (ms). */
const CONSENT_UPDATE_WAIT_MS = 500;

let defaultsEmitted = false;

function allDenied(): Record<string, ConsentModeValue> {
  const denied: Record<string, ConsentModeValue> = {};
  for (const signal of ALL_CONSENT_MODE_SIGNALS) {
    denied[signal] = "denied";
  }
  return denied;
}

/**
 * Emit Consent Mode v2 state. The all-denied `default` has to precede any tag
 * firing — Google ignores a `default` that arrives after the fact — so it goes
 * out once, before the loader below ever runs.
 */
function applyConsentMode(state: ConsentState): void {
  if (typeof window === "undefined") return;

  if (!defaultsEmitted) {
    defaultsEmitted = true;
    gtag("consent", "default", { ...allDenied(), wait_for_update: CONSENT_UPDATE_WAIT_MS });
  }

  // An undecided visitor grants nothing, whatever their provider may have
  // pre-populated.
  const granted = state.decided ? state.granted : new Set([ConsentGroup.Necessary]);
  gtag("consent", "update", consentModeState(granted));
}

/**
 * Idempotent GTM loader. Only ever called once consent allows it — there is no
 * server-rendered snippet, so this is the single entry point for the container.
 */
export function loadGtm(gtmId: string): void {
  if (!gtmId || typeof window === "undefined") return;

  const w = window as GtmWindow;
  if (w.__gtmLoaded) return;
  w.__gtmLoaded = true;

  w.dataLayer = w.dataLayer || [];
  w.dataLayer.push({ "gtm.start": Date.now(), event: "gtm.js" });

  const firstScript = document.getElementsByTagName("script")[0];
  const gtmScript = document.createElement("script");
  gtmScript.async = true;
  gtmScript.src = `https://www.googletagmanager.com/gtm.js?id=${gtmId}`;
  firstScript?.parentNode?.insertBefore(gtmScript, firstScript);
}

/**
 * Push a dataLayer event so GTM tags can trigger off it (LinkedIn/Reddit/Google
 * Ads conversions live in the container, not here — see docs/marketing-pixels.md).
 *
 * Only pushes once the container is actually loaded. A push made while consent
 * is denied would sit in `dataLayer` and fire retroactively the moment GTM
 * starts, attributing a conversion the visitor had declined.
 */
export function pushDataLayerEvent(event: string, params: Record<string, unknown> = {}): void {
  if (typeof window === "undefined") return;

  const w = window as GtmWindow;
  if (!w.__gtmLoaded) return;
  w.dataLayer?.push({ event, ...params });
}

/**
 * Withdrawal can only be honoured by discarding the page. GTM has no teardown
 * API, and by the time consent changes the container has already injected tags
 * that ignore Consent Mode (LinkedIn, Meta, Hotjar) — left alone they keep
 * running and setting cookies for the rest of the session. Reloading is what
 * OneTrust's own default handler does, for this same reason.
 *
 * `__gtmLoaded` is per page load, so the reloaded page can't reload again.
 */
function unloadGtm(): void {
  if (typeof window === "undefined") return;

  const w = window as GtmWindow;
  if (!w.__gtmLoaded) return;
  w.__gtmLoaded = false;
  window.location.reload();
}

/**
 * Single reaction to a consent change: publish the Consent Mode signals, then
 * start or tear down the container. Consent Mode alone isn't enough of a gate —
 * it only binds tags that implement it, and a GTM container can hold
 * third-party tags (LinkedIn, Meta, Hotjar) that don't.
 *
 * Undecided is handled by the same path as a withdrawal: a record that reverts
 * to undecided (a cleared OneTrust cookie, say) grants nothing.
 */
export function syncGtmWithConsent(state: ConsentState): void {
  applyConsentMode(state);

  const allowed = state.decided && GTM_GROUPS.some(group => state.granted.has(group));
  if (!allowed) {
    unloadGtm();
    return;
  }
  loadGtm(getPublicGtmId());
}
